import React, { useEffect, useMemo, useState } from 'react';
import { Button, Dropdown, Input } from 'antd';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { clearAuthState } from 'store/slices/authSlice';
import { clearGlobalOrgState } from 'store/slices/globalOrgSlice';
import { AppDispatch, RootState } from 'store/store';
import { clearUsersState, describeUser } from 'store/slices/usersSlice';
import { formatFullName } from 'utils/utils';
import ProfileDrawer from './profile/ProfileDrawer';
import { clearVisitorSearchState, searchVisitors } from 'store/slices/visitorSearchSlice';
import { clearLocationsState } from 'store/slices/locationsSlice';
import { clearAgreementsState } from 'store/slices/agreementsSlice';
import { clearDenyState } from 'store/slices/denySlice';
import { clearKiosksState } from 'store/slices/kiosksSlice';
import { clearScopeResourcesState } from 'store/slices/scopeResourcesSlice';
import { clearRedFlagState } from 'store/slices/redFlagSlice';
import { clearRolesState } from 'store/slices/rolesSlice';
import { clearUserActivityState } from 'store/slices/userActivitySlice';
import { clearVisitorTypesState } from 'store/slices/visitorTypesSlice';
import { clearVisitorsState } from 'store/slices/visitorsSlice';
import { clearVisitorWorkflowsState } from 'store/slices/visitorWorkflowsSlice';
import { clearVisitsState } from 'store/slices/visitsSlice';
import { clearVisitorInvitationState } from 'store/slices/visitorInvitationSlice';
import { profileIcon } from 'utils/visitorsHelper';
import { persistor } from 'store/store';
import { useConfirmModal } from 'utils/customHooks/useConfirmModal';
import { HelpMenu } from 'components/customAntd/inVision - Platinum Assets/HelpMenu/HelpMenu';
import { MenuOutlined, SearchOutlined } from '@ant-design/icons';
import AdvancedSearchModal from './AdvancedSearchModal';
import { setDarkMode } from 'store/slices/themeSlice';
import { useTranslation } from 'react-i18next';

interface AppHeaderProps {
    showSideMenu: boolean;
    onToggleMenu: () => void;
    isMobile: boolean;
}

const AppHeader: React.FC<AppHeaderProps> = ({
    showSideMenu,
    onToggleMenu,
    isMobile
}) => {
    const { Search } = Input;

    // State hooks
    const [drawerVisible, setDrawerVisible] = useState(false);
    const [isAdvancedSearchVisible, setIsAdvancedSearchVisible] = useState(false);
    const [loading, setLoading] = useState(false);

    // Navigation hook
    const navigate = useNavigate();

    // Dispatch hook
    const dispatch: AppDispatch = useDispatch();

    // Selector hooks
    const orgId = useSelector((state: RootState) => state.globalOrg.globalOrgId);
    const { auth } = useSelector((state: RootState) => state.auth);
    const { globalUser } = useSelector((state: RootState) => state.users);
    const { globalLocation } = useSelector((state: RootState) => state.locations);

    const isDarkMode = useSelector((state: RootState) => state.theme.isDarkMode);

    // Directly access the updated org name from the Redux auth state
    const selectedOrgName = auth.data[0].tokenScopeList.find(scope => Number(scope.org.id) === Number(orgId))?.org.name;

    const { t } = useTranslation();

    // Fetch user details only when orgId changes
    useEffect(() => {
        if (orgId && auth.data[0]) {
            const selectedOrg = auth.data[0].tokenScopeList.find(scope => Number(scope.org.id) === Number(orgId));
            if (selectedOrg && selectedOrg.user) {
                const userId = selectedOrg.user.id;
                dispatch(describeUser({ orgId, userId, global: true }));
            }
        }
    }, [orgId, auth.data, dispatch]);

    const userFullName = useMemo(() => {
        if (globalUser && globalUser.identity) {
            return formatFullName(globalUser?.identity.firstName, globalUser?.identity.middleName, globalUser?.identity.lastName);
        }
    }, [globalUser]);

    const createUserAvatarIcon = (size: number, marginRight: number) => {
        const nameOrEmail = userFullName || globalUser?.identity.email || "";
        return profileIcon({
            fullName: nameOrEmail,
            size: size,
            color: "transparent",
            textColor: isDarkMode ? "#FFFFFF" : "#000000",
            shape: "square",
            marginRight: marginRight,
        });
    };

    // eslint-disable-next-line
    const userAvatarIcon40 = useMemo(() => createUserAvatarIcon(34, 0), [userFullName, globalUser, isDarkMode]);
    // eslint-disable-next-line
    const userAvatarIcon70 = useMemo(() => createUserAvatarIcon(70, 15), [userFullName, globalUser, isDarkMode]);

    const confirmModal = useConfirmModal();

    const onLogout = async () => {
        confirmModal({
            title: t('appHeader.logOutTitle'),
            content: t('appHeader.logOutContent'),
            okText: t('appHeader.logOutOk'),
            cancelText: t('appHeader.logOutCancel'),
            onOk: async () => {
                // Clear state
                await Promise.all([
                    dispatch(clearAgreementsState()),
                    dispatch(clearAuthState()),
                    dispatch(clearDenyState()),
                    dispatch(clearGlobalOrgState()),
                    dispatch(clearKiosksState()),
                    dispatch(clearLocationsState()),
                    dispatch(clearRedFlagState()),
                    dispatch(clearRolesState()),
                    dispatch(clearScopeResourcesState()),
                    dispatch(clearUserActivityState()),
                    dispatch(clearVisitorInvitationState()),
                    dispatch(clearVisitorSearchState()),
                    dispatch(clearVisitorsState()),
                    dispatch(clearVisitorTypesState()),
                    dispatch(clearVisitorWorkflowsState()),
                    dispatch(clearVisitsState()),
                    dispatch(clearUsersState()),
                ]);
                localStorage.clear();

                await persistor.purge();
                dispatch(setDarkMode(isDarkMode));
                await persistor.flush();

                // Remove token from localStorage
                localStorage.removeItem('authToken');
                localStorage.removeItem('identityId');
                localStorage.removeItem('createdAt');
                localStorage.removeItem('expiresAt');
                localStorage.removeItem('tokenScopeList');

                // Navigate to login page
                navigate('/login');
            },
        });
    };

    const menu = {
        items: [
            {
                key: 'profileHeader',
                label: (
                    <div style={{ display: 'flex', alignItems: 'center', padding: '10px 10px 10px 0', borderBottom: '1px solid #f0f0f0', width: '250px' }}>
                        {userAvatarIcon70}
                        <div>
                            <div className="--colorHeaderButtonText" style={{ fontSize: '16px', color: 'var(--colorHeaderButtonText)' }}>{userFullName}</div>
                            <div className="--colorHeaderButtonText" style={{ fontSize: '12px', color: 'var(--colorHeaderButtonText)' }}>{globalUser?.identity.email}</div>
                            <div className="--colorHeaderButtonText" style={{ fontSize: '12px', color: 'var(--colorHeaderButtonText)' }}>{t('appHeader.orgPrefix')}:{selectedOrgName}</div>
                        </div>
                    </div>
                ),
                disabled: true,
            },
            {
                key: '1',
                label: (
                    <Button type="primary" block onClick={() => setDrawerVisible(true)}>
                        {t('appHeader.profile')}
                    </Button>
                ),
            },
            {
                key: '2',
                label: (
                    <Button block danger onClick={onLogout}>
                        {t('appHeader.logOut')}
                    </Button>
                ),
            },
        ]
    };

    const onSearch = async (value: string) => {
        if (value && globalLocation?.id) {
            setLoading(true);
            await dispatch(searchVisitors({
                orgId,
                siteId: globalLocation.id,
                searchString: value.trim() ? value.trim() : ' ',
            }));
            setLoading(false);
            navigate('/visitorSearchResults');
        }
    };


    return (
        <div style={{
            padding: '16px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            height: "68px",
            backgroundColor: "var(--colorBgContainer)"

        }}>
            <div style={{
                flex: 1,
                display: 'flex',
                justifyContent: 'flex-start',
                paddingLeft: '16px'
            }}>
                {isMobile && !showSideMenu && (
                    <Button
                        type="text"
                        icon={<MenuOutlined />}
                        onClick={onToggleMenu}
                        style={{
                            background: 'var(--colorUtnButton)',
                            color: 'var(--colorTextBase)'
                        }}
                    />
                )}
            </div>

            {/* Center Section - Search Bar */}
            {!isMobile && (
                <div style={{
                    flex: 1,
                    maxWidth: 600,
                    minWidth: 200,
                    transition: 'all 0.3s',
                    display: 'flex',
                    justifyContent: 'center'
                }}>
                    <Search
                        placeholder={t('appHeader.searchVisitorPlaceholder')}
                        allowClear
                        style={{
                            width: '100%',
                            maxWidth: 400,
                        }}
                        onSearch={onSearch}
                        loading={loading}
                    />
                </div>
            )}

            <div style={{
                flex: 1,
                display: 'flex',
                justifyContent: 'flex-end',
                paddingRight: '16px'
            }}>
                <Button
                    type="text"
                    icon={<SearchOutlined />}
                    onClick={() => setIsAdvancedSearchVisible(true)}
                />
                <HelpMenu />
                <Dropdown menu={menu} trigger={['click']} placement="bottomRight">
                    <Button
                        type="text"
                        onClick={e => e.preventDefault()}
                        style={{ padding: 0, width: '36px', height: '36px' }}
                    >
                        {userAvatarIcon40}
                    </Button>
                </Dropdown>
            </div>

            {drawerVisible && (
                <ProfileDrawer
                    open={drawerVisible}
                    onClose={() => {
                        setDrawerVisible(false);
                    }}
                />
            )}

            {(isAdvancedSearchVisible) && (
                <AdvancedSearchModal
                    open={isAdvancedSearchVisible}
                    onCancel={() => setIsAdvancedSearchVisible(false)}
                    onSearch={async (values) => {
                        setLoading(true);
                        await dispatch(searchVisitors({
                            orgId,
                            siteId: values.siteId,
                            firstName: values.firstName ? values.firstName.trim() : undefined,
                            lastName: values.lastName ? values.lastName.trim() : undefined,
                            middleName: values.middleName ? values.middleName.trim() : undefined,
                            startDate: values.startDate,
                            endDate: values.endDate,
                        }));
                        setLoading(false);
                        setIsAdvancedSearchVisible(false);
                        navigate('/visitorSearchResults');
                    }}
                    searchLoading={loading}
                />
            )}
        </div>
    );
}

export default AppHeader;